import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const ProductFeaturesEditor = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const queryClient = useQueryClient();

  const { data: features, isLoading } = useQuery({
    queryKey: ['product-features'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_features')
        .select('*')
        .order('created_at');
      
      if (error) throw error;
      return data;
    },
  });

  const handleFileUpload = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('legal_documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('legal_documents')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const addFeatureMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const feature = {
        title: String(formData.get('title')),
        description: String(formData.get('description')),
        feature_type: String(formData.get('feature_type')),
        gif_url: '',
      };

      if (selectedFile) {
        feature.gif_url = await handleFileUpload(selectedFile);
      }

      const { error } = await supabase
        .from('product_features')
        .insert([feature]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-features'] });
      toast.success('Feature added successfully');
      setSelectedFile(null);
    },
    onError: (error) => {
      toast.error('Failed to add feature: ' + error.message);
    }
  });

  const updateFeatureMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FormData }) => {
      const updates: any = {
        title: String(formData.get('title')),
        description: String(formData.get('description')),
        feature_type: String(formData.get('feature_type')),
      };

      if (selectedFile) {
        updates.gif_url = await handleFileUpload(selectedFile);
      }

      const { error } = await supabase
        .from('product_features')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-features'] });
      toast.success('Feature updated successfully');
      setSelectedFile(null);
    },
    onError: (error) => {
      toast.error('Failed to update feature: ' + error.message);
    }
  });

  const deleteFeatureMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('product_features')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-features'] });
      toast.success('Feature deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete feature: ' + error.message);
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              try {
                await addFeatureMutation.mutateAsync(new FormData(e.currentTarget));
                (e.target as HTMLFormElement).reset();
              } finally {
                setIsSubmitting(false);
              }
            }} 
            className="space-y-4"
          >
            <div>
              <Label>Title</Label>
              <Input name="title" required />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea name="description" required rows={3} />
            </div>

            <div>
              <Label>Feature Image</Label>
              <Input
                type="file"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setSelectedFile(e.target.files[0]);
                  }
                }}
                accept="image/*"
              />
            </div>

            <div>
              <Label>Feature Type</Label>
              <Select name="feature_type" defaultValue="core">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="core">Core</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Feature
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {features?.map((feature) => (
          <Card key={feature.id}>
            <CardContent className="pt-6">
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  setIsSubmitting(true);
                  try {
                    await updateFeatureMutation.mutateAsync({
                      id: feature.id,
                      formData: new FormData(e.currentTarget)
                    });
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                className="space-y-4"
              >
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input name="title" defaultValue={feature.title} required />
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea 
                      name="description" 
                      defaultValue={feature.description} 
                      required 
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Feature Image</Label>
                    <div className="flex items-center gap-4">
                      <img
                        src={feature.gif_url}
                        alt={feature.title}
                        className="w-32 h-20 object-cover rounded"
                      />
                      <Input
                        type="file"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            setSelectedFile(e.target.files[0]);
                          }
                        }}
                        accept="image/*"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Feature Type</Label>
                    <Select name="feature_type" defaultValue={feature.feature_type}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="core">Core</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => deleteFeatureMutation.mutate(feature.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};